import { usePostRegistrationMutation, usePatchRegistrationMutation, useVerifyOtpMutation, useResendOtpMutation } from "../../../services/signup/registration";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export const STEP_FIELDS = {
  1: ["firstName", "lastName", "email", "phone", "password", "countryCode"],
  2: ["companyName", "city", "address", "country", "timezone", "about"],
  3: ["domain", "brandColor"],
  4: ["otp"],
};

export const COLORS = [
  "#D90F0F",
  "#3B82F6",
  "#10B981",
  "#8B5CF6",
  "#F97316",
];

export const useSignupFlow = ({ 
  currentStep, 
  setCurrentStep, 
  setCurrentTab, 
  registrationToken, 
  setRegistrationToken, 
  planId, 
  quantity,
  inviteToken,
  couponCode,
  values,
  touched,
  setTouched,
  validateForm,
  setFieldError,
  setFieldTouched
}) => {
  const { t } = useTranslation('signup');

  const [postRegistration, { isLoading: isPosting }] = usePostRegistrationMutation();
  const [patchRegistration, { isLoading: isPatching }] = usePatchRegistrationMutation();
  const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();

  const isSubmitting = isPosting || isPatching || isVerifying;

  const handleResendOtp = async () => {
    try {
      await resendOtp({ token: registrationToken }).unwrap();
      toast.success(t("otp_resent", "Verification code resent"));
    } catch (err) {
      toast.error(err?.data?.error || t("error"));
    }
  };

  const handleNext = async () => {
    const errors = await validateForm();
    
    // Filter only current step errors
    const stepErrors = STEP_FIELDS[currentStep]?.filter(
      (field) => errors[field]
    ) || [];

    if (stepErrors.length === 0) {
      try {
        if (currentStep === 1) {
          const payload = {
            plan_id: planId,
            seat_count: quantity,
            ...(inviteToken && { invite_token: inviteToken }),
            owner: {
              first_name: values.firstName,
              last_name: values.lastName,
              email: values.email,
              password: values.password,
              country_code: values.countryCode,
              phone_number: values.phone,
            },
            coupon_code: couponCode,
          };

          if (!registrationToken) {
            const res = await postRegistration(payload).unwrap();
            if (res && res?.token) {
              setRegistrationToken(res.token);
            }
          } else {
            await patchRegistration({ token: registrationToken, data: payload }).unwrap();
          }
        } else if (currentStep === 2) {
          const payload = {
            company: {
              company_name: values.companyName,
              legal_company_name: values.companyName,
              address: values.address,
              city: values.city,
              country: values.country,
              timezone: values.timezone,
              company_description: values.about,
            }
          };
          await patchRegistration({ token: registrationToken, data: payload }).unwrap();
        } else if (currentStep === 3) {
          const payload = new FormData();
          payload.append("branding[domain]", values.domain);
          payload.append("branding[brand_color]", values.brandColor);
          if (values.logo) {
            payload.append("branding[logo]", values.logo);
          } else {
            payload.append("branding[logo]", "");
          }
          await patchRegistration({ token: registrationToken, data: payload }).unwrap();
        } else if (currentStep === 4) {
          await verifyOtp({ token: registrationToken, otp_code: values.otp }).unwrap();
        }

        if (currentStep < 4) {
          // Clear touched for next step fields to prevent auto-error visibility
          const nextStepFields = STEP_FIELDS[currentStep + 1] || [];
          const newTouched = { ...touched };
          nextStepFields.forEach(field => {
            newTouched[field] = false;
          });
          setTouched(newTouched);
          setCurrentStep((prev) => prev + 1);
        } else {
          setCurrentTab((prev) => prev + 1);
        }
      } catch (err) {
        let errorMsg = Array.isArray(err?.data?.error) ? err?.data?.error?.[0] : err?.data?.error || err?.data?.message || t("error");
        toast.error(errorMsg);
      }
    } else {
      // Mark only current step fields as touched
      const touchedFields = {};
      STEP_FIELDS[currentStep]?.forEach((field) => {
        touchedFields[field] = true;
      });
      setTouched({ ...touched, ...touchedFields });
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  return {
    handleNext,
    handlePrev,
    handleResendOtp,
    isSubmitting,
    isResending
  };
};
